var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Autocomplete } from '@material-ui/lab';
import { componentMessages } from '../../component-translations';
import { useDebouncedCallback } from '../Debounce/DebounceCallback';
import { PaperComponent } from './PaperComponent';
import { RenderInput } from './RenderInput';
import { RenderOption } from './RenderOption';
const useStyles = makeStyles({
    fullWidth: {
        width: '100%'
    }
});
const DEFAULT_LIMIT = 10;
const DEFAULT_DEBOUNCE = 500;
function CheckitAutocomplete(props) {
    var _a;
    const { formatMessage } = useIntl();
    const { className, clearOnSelect = false, debounceDelay, fullWidth, initialValue, inputValue, numberOfResults = DEFAULT_LIMIT, useCache = true, getOptions, onChange, onInputChange: handleInputChange = (() => { }), loadingText = formatMessage(componentMessages['autocomplete.loading']), noResultsText = formatMessage(componentMessages['autocomplete.noResults']), moreResultsText = formatMessage(componentMessages['autocomplete.tooManyResults']), placeholder = formatMessage(componentMessages['autocomplete.placeholder']), label = formatMessage(componentMessages['autocomplete.label']) } = props;
    const classes = useStyles();
    const cachedQueries = useRef(new Map());
    const exclusions = useRef(new Set()); // stores props.excluded as a set for quick lookup
    const [isLoading, setIsLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState([]);
    const [value, setValue] = useState(initialValue || null);
    // keep track of the previous value of the `excluded` prop, so we can invalidate the cache when it changes
    const previousExcluded = useRef(undefined);
    const hasExcludedChanged = props.excluded !== previousExcluded.current;
    if (hasExcludedChanged) {
        // update the exclusions set
        exclusions.current = new Set((_a = props.excluded) !== null && _a !== void 0 ? _a : []);
    }
    const handleSearch = useCallback((term = '', context) => __awaiter(this, void 0, void 0, function* () {
        var _b;
        // attempt to load the options from cache for the current search term
        const optionsLoaded = (_b = cachedQueries.current.get(term)) !== null && _b !== void 0 ? _b : yield getOptions(term || '', numberOfResults + exclusions.current.size + 1);
        if (context.isCurrent) {
            // display the options to the user
            const optionsIncluded = optionsLoaded.filter((opt) => !exclusions.current.has(opt.code));
            const optionsVisible = optionsIncluded.slice(0, numberOfResults);
            setHasMoreResults(optionsIncluded.length >= numberOfResults);
            setOptions(optionsVisible);
            setIsLoading(false);
            // and update the cache for next time
            if (useCache) {
                // can cache optionsIncluded rather than optionsLoaded, as the cache will be invalidated
                // if `excluded` changes anyways, meaning that if the cached value is loaded then the
                // exclusions are guaranteed to not have changed
                cachedQueries.current.set(term, optionsIncluded);
            }
        }
    }), [getOptions, numberOfResults, useCache, setOptions, setIsLoading, setHasMoreResults]);
    const handleDebouncedSearch = useDebouncedCallback((term) => {
        setSearchTerm(term);
    }, debounceDelay || DEFAULT_DEBOUNCE);
    const autoCompleteText = () => {
        if (!isLoading && options.length === 0) {
            return noResultsText;
        }
        return loadingText;
    };
    useEffect(function setInitialValue() {
        setValue(initialValue || null);
    }, [initialValue, setValue]);
    useEffect(function updateSearch() {
        // invalidate the old cache if necessary
        const shouldInvalidateCache = useCache && props.excluded !== previousExcluded.current;
        if (shouldInvalidateCache) {
            // invalidate the old cache whenever exclusions change
            cachedQueries.current.clear();
        }
        // refresh the search results
        // don't bother if the autocomplete isn't open
        if (!isOpen) {
            return;
        }
        const context = { isCurrent: true };
        handleSearch(searchTerm, context);
        // if a newer updateSearch() is necessary, update context to avoid using stale search results
        return () => context.isCurrent = false;
    }, [searchTerm, isOpen, handleSearch, useCache, props.excluded, previousExcluded]);
    useEffect(function updateOptions() {
        if (!value) {
            return;
        }
        const exists = options.find((x) => x.code === value.code);
        if (!exists) {
            setOptions((currentValue) => [value, ...currentValue]);
        }
    }, [value, options, setOptions]);
    const onAutocompleteChange = (event, newValue) => {
        setValue(newValue);
        onChange(event, newValue);
        if (clearOnSelect) {
            // slightly hacky, but material-ui internally calls setValue(newValue) just after
            // calling this onChange handler, meaning that if we set the value to null now that
            // would immediately be overridden and not used
            setTimeout(() => setValue(null), 0);
        }
    };
    const onOpen = () => {
        setIsOpen(true);
        setIsLoading(true);
    };
    const onClosed = () => {
        setIsOpen(false);
    };
    const onInputChange = (e, newValue, reason) => {
        handleInputChange(e, newValue, reason);
        setHasMoreResults(false);
        setIsLoading(true);
        handleDebouncedSearch(newValue);
    };
    // now that everything else is done, update the value of previousExcluded for future renders
    // needs to be below the updateSearch() useEffect, as that uses the value of previousExcluded
    useEffect(() => void (previousExcluded.current = props.excluded));
    return (React.createElement(Autocomplete, { PaperComponent: PaperComponent({ moreResults: hasMoreResults, moreResultsText }), renderInput: RenderInput({ className, label, placeholder }), renderOption: RenderOption(), getOptionLabel: (option) => option.name, getOptionSelected: (option, val) => option.code === val.code, onChange: onAutocompleteChange, onClose: onClosed, onInputChange: onInputChange, onOpen: onOpen, options: options, noOptionsText: autoCompleteText(), blurOnSelect: clearOnSelect, className: fullWidth ? classes.fullWidth : '', inputValue: inputValue, popupIcon: null, selectOnFocus: true, value: value }));
}
export default CheckitAutocomplete;
//# sourceMappingURL=SingleAutocomplete.js.map